import React, {
  forwardRef,
  useEffect,
  isValidElement,
  Children,
  useState,
  KeyboardEvent,
  ReactNode,
  useMemo,
} from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import classNames from 'classnames';
import { useMenuContext } from './Menu';
import { MenuItemBaseProps, MenuIcon } from './MenuItem';
import {
  menu_base_css,
  menu_item_base_css,
  menu_group_chevron_css,
  menu_item_content_css,
  menu_item_icon_css,
  menu_item_right_content_css,
  getMenuItemStyles,
  menu_group_container_css,
  menu_group_header_css,
  menu_group_list_css,
} from './styles';
import { IconArrowChevronDown } from '../icons/generated';
import { Stack } from '../stack';
import { BaseText } from '../text';
import { semantic_colors } from '../../foundation';
import { MenuGroupIdContext } from './context';

export interface MenuGroupProps extends Omit<MenuItemBaseProps, 'variant'> {
  label: string;
  /** 메뉴 그룹에 포함할 MenuItem, MenuSection 또는 중첩 요소 */
  children?: ReactNode;
  /** 메뉴 그룹을 열거나 닫을 때 호출되는 함수 */
  onClick?: () => void;
}

const getDescendantMenuIds = (children: ReactNode): string[] => {
  const ids: string[] = [];

  Children.forEach(children, (child) => {
    if (!isValidElement<{ id?: string; children?: ReactNode }>(child)) {
      return;
    }

    if (child.props.id) {
      ids.push(child.props.id);
    }
    if (child.props.children) {
      ids.push(...getDescendantMenuIds(child.props.children));
    }
  });

  return ids;
};

export const MenuGroup = forwardRef<HTMLLIElement, MenuGroupProps>(
  ({ className, id, label, leftIcon, rightContent, children, onClick, ...props }, ref) => {
    const {
      activeMenu,
      activeMenuGroup,
      setActiveMenuGroup,
      openMenuGroupIds,
      toggleMenuGroup,
      expandMenuGroup,
      onMenuGroupOpenChange,
      dense,
    } = useMenuContext();
    const [mounted, setMounted] = useState(false);
    const selected = activeMenuGroup === id;
    const isExpanded = id != null && openMenuGroupIds.includes(id);
    const validChildrenCount = Children.count(children);
    const childMenuItemIds = useMemo(() => getDescendantMenuIds(children), [children]);

    const handleClickMenu = () => {
      toggleMenuGroup(id);
      setMounted(false);
      onClick?.();
    };
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleClickMenu();
      }
    };

    useEffect(() => {
      if (activeMenu && childMenuItemIds.includes(activeMenu)) {
        setActiveMenuGroup(id);
        expandMenuGroup(id);
      } else if (activeMenuGroup === id) {
        // 선택이 다른 그룹으로 옮겨가면 이전 그룹의 선택 표시를 스스로 지운다.
        setActiveMenuGroup(undefined);
      }
    }, [activeMenu, activeMenuGroup, childMenuItemIds, id]);

    useEffect(() => {
      setMounted(true);
    }, []);

    return (
      <li role='none' className={classNames('pds-menu-group', className)} {...props} ref={ref}>
        <Stack
          role='menuitem'
          tabIndex={0}
          aria-current={selected ? 'page' : undefined}
          aria-expanded={isExpanded}
          aria-haspopup={validChildrenCount > 0}
          direction='row'
          css={[menu_item_base_css, menu_group_header_css, getMenuItemStyles(dense)]}
          align='center'
          gap={6}
          onClick={handleClickMenu}
          onKeyDown={handleKeyDown}
        >
          {leftIcon && (
            <span css={menu_item_icon_css}>
              <MenuIcon selected={selected}>{leftIcon}</MenuIcon>
            </span>
          )}
          <Stack direction='row' gap={0} mr='auto' align='center' css={menu_item_content_css}>
            <BaseText
              typography={isExpanded ? 'Body_13_SemiBold' : 'Body_13_Medium'}
              color={semantic_colors.content.primary}
            >
              {label}
            </BaseText>
            {rightContent && <div css={menu_item_right_content_css}>{rightContent}</div>}
          </Stack>
          <motion.div
            css={menu_group_chevron_css}
            animate={{
              rotate: isExpanded ? 180 : 0,
            }}
            transition={{ duration: 0.25 }}
          >
            <IconArrowChevronDown
              size={14}
              color={isExpanded ? semantic_colors.content.primary : semantic_colors.content.quaternary}
            />
          </motion.div>
        </Stack>
        <AnimatePresence initial={false}>
          {isExpanded && (
            <motion.div
              initial={selected && mounted ? 'expand' : 'close'}
              animate='expand'
              exit='close'
              css={menu_group_container_css}
              variants={{
                expand: { height: 'auto' },
                close: { height: 0 },
              }}
              transition={{ type: 'easeIn', duration: 0.25 }}
              onAnimationComplete={() => onMenuGroupOpenChange?.(id)}
            >
              <MenuGroupIdContext.Provider value={id}>
                <ul role='menu' css={[menu_base_css, menu_group_list_css]}>
                  {children}
                </ul>
              </MenuGroupIdContext.Provider>
            </motion.div>
          )}
        </AnimatePresence>
      </li>
    );
  },
);
